// src/components/PermissionPopover.tsx
//
// Global permission popover — automatically appears when the user hovers over
// any disabled button that has a title starting with "Brak uprawnień".
// No changes required to existing button code.

import { useEffect, useRef, useState } from "react";

type PopoverState = {
  x: number;
  y: number;
  message: string;
} | null;

const PERM_PREFIX = "Brak uprawnień";

export default function PermissionPopover() {
  const [popover, setPopover] = useState<PopoverState>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function onMouseEnter(e: MouseEvent) {
      const target = e.target as HTMLElement;
      const btn = target.closest("button[disabled], button:disabled") as HTMLButtonElement | null;
      if (!btn) return;

      const title = btn.getAttribute("title") ?? "";
      if (!title.startsWith(PERM_PREFIX)) return;

      // Suppress native browser tooltip by temporarily removing the title
      btn.setAttribute("data-perm-title", title);
      btn.removeAttribute("title");

      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);

      const rect = btn.getBoundingClientRect();
      setPopover({
        x: rect.left + rect.width / 2,
        y: rect.top - 8,
        message: title,
      });
    }

    function onMouseLeave(e: MouseEvent) {
      const target = e.target as HTMLElement;
      const btn = target.closest("button") as HTMLButtonElement | null;
      if (!btn) return;

      // Restore title attribute
      const saved = btn.getAttribute("data-perm-title");
      if (saved) {
        btn.setAttribute("title", saved);
        btn.removeAttribute("data-perm-title");
      }

      hideTimerRef.current = setTimeout(() => setPopover(null), 100);
    }

    document.addEventListener("mouseover", onMouseEnter, true);
    document.addEventListener("mouseout", onMouseLeave, true);
    return () => {
      document.removeEventListener("mouseover", onMouseEnter, true);
      document.removeEventListener("mouseout", onMouseLeave, true);
    };
  }, []);

  if (!popover) return null;

  // Extract permission key from message like "Brak uprawnień: pms.reservations.create"
  const permKey = popover.message.includes(": ")
    ? popover.message.split(": ").slice(1).join(": ")
    : null;

  return (
    <div
      className="xPermPopover"
      style={{
        left: popover.x,
        top: popover.y,
      }}
      role="tooltip"
    >
      <div className="xPermPopoverIcon" aria-hidden="true">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </div>
      <div className="xPermPopoverBody">
        <div className="xPermPopoverTitle">Brak uprawnień</div>
        {permKey && <div className="xPermPopoverKey">{permKey}</div>}
      </div>
    </div>
  );
}
